import React, { useEffect, useState } from "react";
import CommonSlider from "../../../common/CommonSlider";
import CustomInput from "../../../common/CustomInput";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../../../common/Button";
import {
  contactOptions,
  countryOptions,
  relationshipTypeThirdStepOptions,
  sameAsPersonalInfoCheckBoxOptions,
  stateOptions,
} from "../../create/options";
import {
  isFieldError,
  isNameError,
  isValidEmail,
  isValidPhoneNumber,
  isValidPostalCode,
} from "../../../../helpers/input-validations";
import { useDispatch } from "react-redux";
import {
  capitalizeFirstLetter,
  extractPhoneNumber,
} from "../../../../helpers/functions";
import {
  createCaregiverDetails,
  updateCaregiverDetails,
} from "../../../../redux/modules/patient/actions";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";

const CaregiverCreate = ({
  open,
  onClose,
  patient,
  careGiver,
  patientId,
  create = false,
}) => {
  const dispatch = useDispatch();
  const [lastNameCaregiver, setLastNameCaregiver] = useState(
    create ? "" : careGiver.lastName
  );
  const [middleNameCaregiver, setMiddleNameCaregiver] = useState(
    create ? "" : careGiver.middleName
  );
  const [firstNameCaregiver, setFirstNameCaregiver] = useState(
    create ? "" : careGiver.firstName
  );
  const [relationshipTypeCaregiver, setRelationshipTypeCaregiver] = useState(
    create ? "" : careGiver.relationship
  );
  const [contactOptionsCaregiver, setContactOptionsCaregiver] = useState(
    create ? "" : careGiver.primaryContact ? "primary" : "secondary"
  );
  const [phoneCountryCareGiver, setPhoneCountryCaregiver] = useState(
    create ? "+!" : careGiver.primaryPhoneCountryCode
  );
  const [secondaryPhoneCountryCareGiver, setSecondaryPhoneCountryCaregiver] =
    useState(create ? "+1" : careGiver.secondaryPhoneCountryCode);
  const [phoneNumberCaregiver, setPhoneNumberCaregiver] = useState(
    create ? "" : careGiver.primaryPhone
  );
  const [secondaryPhoneNumberCaregiver, setSecondaryPhoneNumberCaregiver] =
    useState(create ? "" : careGiver.secondaryPhone);
  const [emailCaregiver, setEmailCaregiver] = useState(
    create ? "" : careGiver.email
  );
  const [sameAsPersonalInfoCheckBox, setSameAsPersonalInfoCheckBox] =
    useState(false);
  const [addressCaregiver, setAddressCaregiver] = useState(
    create ? "" : careGiver.address
  );
  const [unitCaregiver, setUnitCaregiver] = useState(
    create ? "" : careGiver.unitNumber
  );
  const [poBoxCaregiver, setPoBoxCaregiver] = useState(
    create ? "" : careGiver.poBox
  );
  const [cityCaregiver, setCityCaregiver] = useState(
    create ? "" : careGiver.city
  );
  const [stateCaregiver, setStateCaregiver] = useState(
    create ? "" : careGiver.province
  );
  const [postalCodeCaregiver, setPostalCodeCaregiver] = useState(
    create ? "" : careGiver.postalCode
  );
  const [countryOptionsCaregiver, setCountryOptionsCaregiver] = useState(
    create ? "" : careGiver.country
  );
  const [commentsCaregiver, setCommentsCaregiver] = useState(
    create ? "" : careGiver.comments
  );
  const [lastNameCaregiverError, setLastNameCaregiverError] = useState(false);
  const [firstNameCaregiverError, setFirstNameCaregiverError] = useState(false);
  const [relationshipTypeCaregiverError, setRelationshipTypeCaregiverError] =
    useState(false);
  const [phoneNumberCaregiverError, setPhoneNumberCaregiverError] =
    useState(false);
  const [openPrimaryContact, setOpenPrimaryContact] = useState(false);
  const [submitCliked, setsubmitCliked] = useState(false);

  const fieldsToCheck = [
    {
      field: lastNameCaregiver,
      checkFn: isNameError,
      setErrorFn: setLastNameCaregiverError,
      alter: false,
    },
    {
      field: firstNameCaregiver,
      checkFn: isNameError,
      setErrorFn: setFirstNameCaregiverError,
      alter: false,
    },
    {
      field: relationshipTypeCaregiver,
      checkFn: isFieldError,
      setErrorFn: setRelationshipTypeCaregiverError,
      alter: false,
    },
    {
      field: phoneNumberCaregiver,
      checkFn: isValidPhoneNumber,
      setErrorFn: setPhoneNumberCaregiverError,
      alter: true,
    },
  ];

  useEffect(() => {
    if (submitCliked) {
      fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
        const hasError = alter ? !checkFn(field) : checkFn(field);
        setErrorFn(hasError);
      });
    }
  }, [...fieldsToCheck.map(({ field }) => field)]);

  const manageStep = () => {
    setsubmitCliked(true);
    let allFieldsValid = true;
    fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
      const hasError = alter ? !checkFn(field) : checkFn(field);
      setErrorFn(hasError);

      if (hasError) {
        allFieldsValid = false;
      }
    });
    if (allFieldsValid) {
      let caregiverUpdateObject = {
        firstName: firstNameCaregiver,
        middleName: middleNameCaregiver,
        lastName: lastNameCaregiver,
        relationship: relationshipTypeCaregiver,
        contactInfo: {
          primaryPhone: extractPhoneNumber(phoneNumberCaregiver) || null,
          secondaryPhone:
            extractPhoneNumber(secondaryPhoneNumberCaregiver) || null,
          secondaryPhoneCountryCode: secondaryPhoneCountryCareGiver,
          primaryPhoneCountryCode: phoneCountryCareGiver,
          email: emailCaregiver || null,
          address: addressCaregiver || null,
          city: cityCaregiver || null,
          province: stateCaregiver || null,
          postalCode: postalCodeCaregiver?.replace(/\s/g, "") || null,
          primaryContact: contactOptionsCaregiver === "primary" ? true : false, // Set to true or false as needed
          country: countryOptionsCaregiver || null,
          poBox: poBoxCaregiver || null,
          unitNumber: unitCaregiver || null,
          comments: commentsCaregiver || null,
        },
      };
      if (create) {
        dispatch(createCaregiverDetails(patientId, caregiverUpdateObject));
      } else {
        dispatch(updateCaregiverDetails(careGiver.id, caregiverUpdateObject));
      }
      onClose(false);
    }
  };
  const handleInputChange = (field, value) => {
    // Clear error message when input changes
    // Update corresponding state value
    switch (field) {
      case "lastNameCaregiver":
        setLastNameCaregiver(value);
        break;
      case "middleNameCaregiver":
        setMiddleNameCaregiver(value);
        break;
      case "firstNameCaregiver":
        setFirstNameCaregiver(value);
        break;
      case "relationshipTypeCaregiver":
        setRelationshipTypeCaregiver(value);
        break;
      case "phoneNumberCaregiver":
        setPhoneNumberCaregiver(value);
        break;
      // Handle other fields
      default:
        break;
    }
  };

  return (
    <CommonSlider  open={open} onClose={onClose}>
      <div className="flex flex-col flex-1 h-full overflow-y-auto ">
        <div className="relative flex flex-col flex-1">
          {/* Header */}
          <div className="px-8 py-6 bg-gray-20">
            <div className="flex items-start justify-between space-x-3">
              <div className="flex flex-col">
                <p className="font-bold text-gray-800 text-display-xs">
                  {create ? "Add " : "Edit "}Alternate Caregiver Information
                </p>
                <p className="text-body-md text-gray-700">
                  {patient.lastName}, {patient.firstName}
                </p>
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                  onClick={() => onClose(false)}
                >
                  <span className="sr-only">Close panel</span>
                  <XMarkIcon
                    className="w-5 h-5 m-auto"
                    strokeWidth="2"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-col justify-between flex-1 px-8 mt-4">
            <div className="grid grid-cols-12 gap-3 ">
              <CustomInput
                name="lastNameCaregiver"
                className="col-span-4"
                label="Last Name"
                value={lastNameCaregiver}
                error={lastNameCaregiverError}
                capitalizeFirstLetter={true}
                onChange={(e) =>
                  handleInputChange("lastNameCaregiver", e.target.value)
                }
                placeholder="Enter last name"
                type="text"
                required
              />
              <CustomInput
                name="middleNameCaregiver"
                className="col-span-4"
                label="Middle Name"
                value={middleNameCaregiver}
                capitalizeFirstLetter={true}
                onChange={(e) =>
                  handleInputChange("middleNameCaregiver", e.target.value)
                }
                placeholder="Enter middle name"
                type="text"
              />
              <CustomInput
                name="firstNameCaregiver"
                className="col-span-4"
                label="First Name"
                value={firstNameCaregiver}
                error={firstNameCaregiverError}
                capitalizeFirstLetter={true}
                onChange={(e) =>
                  handleInputChange("firstNameCaregiver", e.target.value)
                }
                placeholder="Enter first name"
                type="text"
                required
              />
              <CustomInput
                options={relationshipTypeThirdStepOptions}
                name="RelationshipTypeCaregiver"
                className="col-span-12"
                label="Relationship"
                type="dropdown"
                onChange={(e) =>
                  handleInputChange("relationshipTypeCaregiver", e.value)
                }
                value={relationshipTypeCaregiver}
                error={relationshipTypeCaregiverError}
                required
              />
              <CustomInput
                options={contactOptions}
                type="radio"
                name="contactOptionsCaregiver"
                className="col-span-12"
                onChange={(e) => {
                  setContactOptionsCaregiver(e.target.value);
                  if (
                    !careGiver.primaryContact &&
                    e.target.value === contactOptions[0].value
                  )
                    setOpenPrimaryContact(true);
                }}
                value={contactOptionsCaregiver}
              />
              <div className="col-span-12 my-3 bg-gray-100 h-[1px] w-full " />
              <p className="col-span-12 font-medium text-gray-900 text-body-md">
                Contact Information
              </p>
              <CustomInput
                name="phoneNumberCaregiver"
                className="col-span-6"
                label="Primary Phone Number"
                value={phoneNumberCaregiver}
                selectValue={phoneCountryCareGiver}
                handleSelect={(e) => setPhoneCountryCaregiver(e.target.value)}
                error={phoneNumberCaregiverError}
                onChange={(e) =>
                  handleInputChange("phoneNumberCaregiver", e.target.value)
                }
                placeholder="(555) 000-0000"
                type="phone"
                required
              />
              <CustomInput
                name="secondaryPhoneNumberCaregiver"
                className="col-span-6"
                label="Secondary Phone Number"
                placeholder="(555) 000-0000"
                type="phone"
                value={secondaryPhoneNumberCaregiver}
                selectValue={secondaryPhoneCountryCareGiver}
                handleSelect={(e) =>
                  setSecondaryPhoneCountryCaregiver(e.target.value)
                }
                onChange={(e) =>
                  setSecondaryPhoneNumberCaregiver(e.target.value)
                }
                error={
                  secondaryPhoneNumberCaregiver &&
                  !isValidPhoneNumber(secondaryPhoneNumberCaregiver)
                }
              />
              <CustomInput
                name="emailCaregiver"
                className="col-span-12"
                label="Email Address"
                value={emailCaregiver}
                error={emailCaregiver && !isValidEmail(emailCaregiver)}
                onChange={(e) => setEmailCaregiver(e.target.value)}
                placeholder="Enter Email Address"
                type="email"
              />
              <CustomInput
                className="col-span-12 !font-semiboldld"
                options={sameAsPersonalInfoCheckBoxOptions}
                type="checkbox"
                name="sameAsPersonalInfoCheckBox"
                onChange={(e) =>
                  setSameAsPersonalInfoCheckBox(e.target.checked)
                }
                value={sameAsPersonalInfoCheckBox}
              />
              <CustomInput
                name="addressCaregiver"
                className="col-span-12"
                label="Address"
                placeholder=" Enter Street Number & Street Name"
                type="text"
                onChange={(e) => setAddressCaregiver(e.target.value)}
                value={addressCaregiver}
                error={addressCaregiver && isFieldError(addressCaregiver)}
              />
              <CustomInput
                name="unitCaregiver"
                className="col-span-6"
                label="Unit Number"
                onChange={(e) => setUnitCaregiver(e.target.value)}
                value={unitCaregiver}
                error={unitCaregiver && isFieldError(unitCaregiver)}
                placeholder="Enter Unit Number"
                type="text"
              />
              <CustomInput
                name="poBoxCaregiver"
                className="col-span-6"
                label="PO Box"
                onChange={(e) => setPoBoxCaregiver(e.target.value)}
                value={poBoxCaregiver}
                error={poBoxCaregiver && isFieldError(poBoxCaregiver)}
                placeholder="PO Box"
                type="age"
              />
              <CustomInput
                name="cityCaregiver"
                className="col-span-6"
                label="City"
                onChange={(e) => setCityCaregiver(e.target.value)}
                value={cityCaregiver}
                error={cityCaregiver && isFieldError(cityCaregiver)}
                type="text"
                placeholder={"Enter City"}
              />
              <CustomInput
                options={stateOptions}
                name="stateCaregiver"
                className="col-span-6"
                label="Province/State"
                type="dropdown"
                onChange={(e) => setStateCaregiver(e.value)}
                value={stateCaregiver}
                error={stateCaregiver && isFieldError(stateCaregiver)}
              />
              <CustomInput
                name="postalCodeCaregiver"
                className="col-span-6"
                label="Postal Code"
                placeholder="Enter Postal Code"
                type="postalCode"
                onChange={(e) => setPostalCodeCaregiver(e.target.value)}
                value={postalCodeCaregiver}
                error={postalCodeCaregiver && !isValidPostalCode(postalCodeCaregiver)}
              />
              <CustomInput
                options={countryOptions}
                name="countryOptionsCaregiver"
                className="col-span-6"
                label="Country"
                type="dropdown"
                onChange={(e) => setCountryOptionsCaregiver(e.value)}
                value={countryOptionsCaregiver}
                error={
                  countryOptionsCaregiver &&
                  isFieldError(countryOptionsCaregiver)
                }
              />
              <div className="col-span-12 my-1" />
              <CustomInput
                name="commentsCaregiver"
                className="col-span-12"
                label="Comments"
                onChange={(e) => setCommentsCaregiver(e.target.value)}
                value={commentsCaregiver}
                error={commentsCaregiver && isFieldError(commentsCaregiver)}
                type="text"
                placeholder={"Enter Note"}
              />
              <ConfirmationModalNew
                open={openPrimaryContact}
                title={"Primary Contact"}
                type={"primary"}
                confirmText={"Yes, Continue"}
                content={() => (
                  <>
                    Are you sure you want to save{" "}
                    <b>
                      {capitalizeFirstLetter(lastNameCaregiver)},{" "}
                      {capitalizeFirstLetter(firstNameCaregiver)}
                    </b>{" "}
                    as your <b>Primary Contact</b>? This will mean that you will no
                    longer be the <b>Primary Contact</b>.
                  </>
                )
                }
                handleClose={() => setOpenPrimaryContact(false)}
                confirmAction={() => setOpenPrimaryContact(false)}
              />
            </div>
            <div className="flex items-center justify-center py-6">
              <div className="flex items-center justify-center space-x-5">
                <Button
                  className="min-w-[200px]"
                  text={`Cancel`}
                  type="secondary"
                  onClick={() => onClose(false)}
                />
                <Button
                  className="min-w-[200px]"
                  text={`${create ? "Save" : "Update"}`}
                  type="primary"
                  onClick={() => manageStep()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </CommonSlider>
  );
};

export default CaregiverCreate;
